import JsBarcode from "jsbarcode";
import moment from "moment";
import "moment/locale/es";

moment.locale("es");

const mmToPt = (mm) => mm * 2.83465;
const PT_A_MM = 0.3528;
const FACTOR_LINEA = 1.15;

const alturaTextoMm = (fontSizePt, factorLinea = FACTOR_LINEA) => fontSizePt * PT_A_MM * factorLinea;

export const ANCHO_MM_DEFAULT = 60;
const MARGEN_MM = 2;

const PAD_CELDA_FECHA_MM = 0.7;
const GAP_TITULO_VALOR_MM = 0.5;
const MARGIN_BLOQUE_SUPERIOR_MM = 1.5;
const MARGIN_DESC_MM = 1.2;
const MARGIN_LEYENDA_MM = 1.2;
const MARGIN_BARCODE_TOP_MM = 1;
const MARGIN_BARCODE_TEXTO_MM = 0.5;
const PADDING_RECUADRO_MM = 1.5;

const calcularLogoMm = (anchoMm) => Math.min(Math.max(anchoMm * 0.5, 15), 45);

const calcularAltoBarcodeMm = (anchoMm) => Math.min(Math.max(anchoMm * 0.2, 10), 22);

const generarBarcodeDataURL = (valor) => {
  if (!valor) return null;
  if (typeof document === "undefined") return null;
  const JsBarcodeLib =
    (typeof JsBarcode !== "undefined" && JsBarcode) ||
    (typeof window !== "undefined" && window.JsBarcode) ||
    null;
  if (!JsBarcodeLib) return null;
  const canvas = document.createElement("canvas");
  try {
    JsBarcodeLib(canvas, String(valor), {
      format: "CODE128",
      displayValue: false,
      lineColor: "#000",
      width: 1.6,
      height: 70,
      margin: 4,
    });
    return canvas.toDataURL("image/png");
  } catch (e) {
    return null;
  }
};

const GAMMA_OSCURIDAD = 2.2;
const convertirAGrisesBase64 = (base64) =>
  new Promise((resolve) => {
    if (!base64 || typeof document === "undefined") return resolve(base64);
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const d = imageData.data;
        for (let i = 0; i < d.length; i += 4) {
          const gris = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
          const oscuro = Math.round(255 * Math.pow(gris / 255, GAMMA_OSCURIDAD));
          d[i] = d[i + 1] = d[i + 2] = oscuro;
        }
        ctx.putImageData(imageData, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch (e) {
        resolve(base64);
      }
    };
    img.onerror = () => resolve(base64);
    img.src = base64;
  });

const fontSizeDescripcion = (texto) => {
  const len = (texto || "").length;
  if (len <= 30) return 11;
  if (len <= 60) return 10;
  if (len <= 100) return 9;
  return 8;
};

const caracteresPorLinea = (anchoMm) => Math.max(anchoMm * 0.6, 15);

const estimarLineasDescripcion = (descripcion, anchoMm) => {
  const len = (descripcion || "").length;
  if (len === 0) return 1;
  return Math.min(3, Math.max(1, Math.ceil(len / caracteresPorLinea(anchoMm))));
};

// Celda de fecha/peso: título pequeño arriba, valor grande abajo (parrilla logo/datos).
const celdaFecha = (titulo, valor) => ({
  stack: [
    { text: titulo, fontSize: 6.5, bold: true },
    { text: valor || "—", fontSize: 10, bold: true, margin: [0, mmToPt(GAP_TITULO_VALOR_MM), 0, 0] },
  ],
  margin: [mmToPt(1), mmToPt(PAD_CELDA_FECHA_MM), mmToPt(0.7), mmToPt(PAD_CELDA_FECHA_MM)],
});

const construirEtiqueta = (pieza, insumo, empresa, fechaEmpaque, consumoPreferente, leyenda, anchoMm) => {
  const clave = insumo?.clave || insumo?.codigo || "";
  const codigoBarras = insumo?.codigoBarras || "";
  const descripcion = (insumo?.descripcion || "").trim().toUpperCase();
  const unidad = insumo?.unidad?.nombre || "";
  const cantidad = pieza?.cantidad;

  const barcodeDataURL = generarBarcodeDataURL(codigoBarras);
  const anchoBarcodeMm = Math.max(anchoMm - 2 * MARGEN_MM - 6, 20);
  const altoBarcodeMm = calcularAltoBarcodeMm(anchoMm);
  const logoMm = calcularLogoMm(anchoMm);
  const tieneLogo = !!empresa?.logo?.base64Completa;

  // --- Bloque superior: logo (izquierda, ocupa las 3 filas) + Fecha de Empaque / Consumo Preferente / Peso-Cantidad (derecha) ---
  const celdaLogo = tieneLogo
    ? { image: "logoEtiqueta", fit: [mmToPt(logoMm), mmToPt(logoMm)], alignment: "center", rowSpan: 3, margin: [0, mmToPt(2), 0, 0] }
    : { text: "", rowSpan: 3 };

  const bloqueSuperior = {
    table: {
      widths: [mmToPt(logoMm + 4), "*"],
      body: [
        [celdaLogo, celdaFecha("FECHA DE EMPAQUE", fechaEmpaque ? moment(fechaEmpaque).format("DD/MM/YYYY") : "")],
        [{}, celdaFecha("CONSUMO PREFERENTE", consumoPreferente ? moment(consumoPreferente).format("DD/MM/YYYY") : "")],
        [{}, celdaFecha("PESO/CANTIDAD", cantidad != null ? `${cantidad} ${unidad}`.trim() : "")],
      ],
    },
    layout: {
      hLineWidth: (i) => (i === 0 || i === 3 ? 0.75 : 0.5),
      vLineWidth: () => 0.75,
      hLineColor: () => "#000",
      vLineColor: () => "#000",
      paddingLeft: () => 0,
      paddingRight: () => 0,
      paddingTop: () => 0,
      paddingBottom: () => 0,
    },
    margin: [0, 0, 0, mmToPt(MARGIN_BLOQUE_SUPERIOR_MM)],
  };

  const desc = {
    text: descripcion || "",
    fontSize: fontSizeDescripcion(descripcion || ""),
    bold: true,
    alignment: "center",
    lineHeight: 1.1,
    margin: [0, 0, 0, mmToPt(MARGIN_DESC_MM)],
  };

  const bloqueBarcode = {
    stack: [
      barcodeDataURL
        ? {
            image: barcodeDataURL,
            fit: [mmToPt(anchoBarcodeMm), mmToPt(altoBarcodeMm)],
            alignment: "center",
          }
        : {
            text: clave || "-",
            fontSize: 12,
            bold: true,
            alignment: "center",
          },
      {
        text: codigoBarras || clave || "",
        fontSize: 8,
        bold: true,
        characterSpacing: 0.5,
        alignment: "center",
        margin: [0, mmToPt(MARGIN_BARCODE_TEXTO_MM), 0, 0],
      },
    ],
    margin: [0, mmToPt(MARGIN_BARCODE_TOP_MM), 0, 0],
  };

  const contenidoInferior = [desc];
  if (leyenda) {
    contenidoInferior.push({
      text: leyenda.toUpperCase(),
      fontSize: 7,
      bold: true,
      alignment: "center",
      margin: [0, 0, 0, mmToPt(MARGIN_LEYENDA_MM)],
    });
  }
  contenidoInferior.push(bloqueBarcode);

  const recuadroInferior = {
    table: {
      widths: ["*"],
      body: [[{ stack: contenidoInferior, margin: [mmToPt(3), mmToPt(PADDING_RECUADRO_MM), mmToPt(3), mmToPt(PADDING_RECUADRO_MM)] }]],
    },
    layout: {
      hLineWidth: () => 0.75,
      vLineWidth: () => 0.75,
      hLineColor: () => "#000",
      vLineColor: () => "#000",
      paddingLeft: () => 0,
      paddingRight: () => 0,
      paddingTop: () => 0,
      paddingBottom: () => 0,
    },
  };

  return [bloqueSuperior, recuadroInferior];
};

export const calcularAltoAutomaticoMm = (anchoMm, descripcion, tieneLeyenda) => {
  const logoMm = calcularLogoMm(anchoMm);
  const altoBarcodeMm = calcularAltoBarcodeMm(anchoMm);

  const alturaFila = alturaTextoMm(6.5) + GAP_TITULO_VALOR_MM + alturaTextoMm(10) + 2 * PAD_CELDA_FECHA_MM;
  const alturaFilasSuperior = alturaFila * 3;
  const alturaBloqueSuperior = Math.max(logoMm, alturaFilasSuperior) + MARGIN_BLOQUE_SUPERIOR_MM;

  const fontSizeDesc = fontSizeDescripcion(descripcion);
  const lineasDescripcion = estimarLineasDescripcion(descripcion, anchoMm);
  const alturaDescripcion = lineasDescripcion * alturaTextoMm(fontSizeDesc, 1.1) + MARGIN_DESC_MM;

  const alturaLeyenda = tieneLeyenda ? alturaTextoMm(7) + MARGIN_LEYENDA_MM : 0;
  const alturaBarcode = altoBarcodeMm + MARGIN_BARCODE_TOP_MM + alturaTextoMm(8) + MARGIN_BARCODE_TEXTO_MM;
  const alturaRecuadroInferior = 2 * PADDING_RECUADRO_MM + alturaDescripcion + alturaLeyenda + alturaBarcode;

  const margenes = MARGEN_MM * 2;
  const colchon = 5;

  return Math.ceil(margenes + alturaBloqueSuperior + alturaRecuadroInferior + colchon);
};

const ANCHO_MM_MINIMO = 60;

const etiquetaEntregaInsumoPdf = async (data, opciones = {}) => {
  const { insumo, empresa, piezas, fechaEmpaque, consumoPreferente, leyenda } = data;
  const anchoMm = Math.max(opciones.anchoMm || ANCHO_MM_DEFAULT, ANCHO_MM_MINIMO);
  const altoMm = calcularAltoAutomaticoMm(anchoMm, insumo?.descripcion, !!leyenda);
  const piezasValidas = (piezas || []).filter((p) => p && p.cantidad > 0);

  const margen = [mmToPt(MARGEN_MM), mmToPt(MARGEN_MM), mmToPt(MARGEN_MM), mmToPt(MARGEN_MM)];

  const content = [];
  piezasValidas.forEach((pieza, idx) => {
    const etiqueta = construirEtiqueta(pieza, insumo, empresa, fechaEmpaque, consumoPreferente, leyenda, anchoMm);
    if (idx > 0) {
      content.push({ text: "", pageBreak: "before" });
    }
    content.push(...etiqueta);
  });

  if (content.length === 0) {
    content.push({
      text: "No hay piezas para generar etiquetas",
      fontSize: 9,
      alignment: "center",
      margin: [0, 20, 0, 0],
    });
  }

  const dd = {
    pageSize: { width: mmToPt(anchoMm), height: mmToPt(altoMm) },
    pageMargins: margen,
    content,
    defaultStyle: {
      fontSize: 8,
    },
  };

  if (empresa?.logo?.base64Completa) {
    const logoMono = await convertirAGrisesBase64(empresa.logo.base64Completa);
    dd.images = { logoEtiqueta: logoMono };
  }

  return dd;
};

export default etiquetaEntregaInsumoPdf;
